import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Card,
  Stat,
  Button,
  Badge,
  Progress,
  Alert,
  Icon,
  Separator,
  List,
} from '@chakra-ui/react';
import { FiCheck, FiTrendingUp, FiAlertCircle, FiAlertTriangle } from 'react-icons/fi';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { useAuth } from '../hooks/useAuth';
import StripePayment from './StripePayment';
import api from '../services/api';
import { toaster } from './ui/toaster';
import { useTranslation } from 'react-i18next';

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  monthly_price: number;
  annual_price: number;
  reviews_per_month: number;
  max_document_size_mb: number;
  max_documents_per_review: number;
  report_storage_days: number;
  unlimited_standards: boolean;
  features: Record<string, any>;
  is_active: boolean;
  tier: string;
}

interface UsageStats {
  current_month: {
    reviews: number;
    policy_uploads: number;
  };
  total: {
    reviews: number;
  };
  period: {
    start: string;
    end: string;
  };
}

const UserSubscriptionManager: React.FC = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [_isModalOpen, setIsModalOpen] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    console.log('UserSubscriptionManager: user state:', user);
    if (user) {
      console.log('UserSubscriptionManager: Loading data for user:', user.email);
      loadData();
    } else {
      console.log('UserSubscriptionManager: No user available, skipping data load');
    }
  }, [user]);

  const loadData = async () => {
    try {
      console.log('UserSubscriptionManager: Starting data load...');
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      console.log('UserSubscriptionManager: Auth token available:', !!token);

      const [plansRes, usageRes] = await Promise.all([
        api.get('/api/user/subscription-plans'),
        api.get('/api/user/usage')
      ]);

      console.log('UserSubscriptionManager: Plans response:', plansRes.data);
      console.log('UserSubscriptionManager: Usage response:', usageRes.data);

      // Handle plans response - backend returns {plans: [...]}
      setPlans(plansRes.data?.plans || []);

      // Handle usage response - backend returns usage_stats object
      const usageData = usageRes.data?.usage_stats || usageRes.data || null;
      setUsage(usageData);

      console.log('UserSubscriptionManager: Data loaded successfully');
    } catch (error: any) {
      console.error('UserSubscriptionManager: Error loading subscription data:', error);
      console.error('UserSubscriptionManager: Error response:', error.response);
      setError(error.response?.data?.detail || t('admin.content.userSubscription.errors.failedToLoadSubscriptionInformation'));
      toaster.create({
        title: t('admin.content.userSubscription.toasts.errorLoadingSubscriptionData'),
        description: error.response?.data?.detail || t('admin.content.userSubscription.toasts.failedToLoadPlansAndUsage'),
        type: 'error',
      });
      // Set empty arrays/objects to prevent crashes
      setPlans([]);
      setUsage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentPlan = () => {
    if (!user?.subscription_tier || !plans || plans.length === 0) return null;

    // Find plan by matching user's subscription tier with plan's tier field
    return plans.find(plan => plan.tier === user.subscription_tier) || null;
  };

  const getUsagePercentage = () => {
    if (!usage?.current_month?.reviews || !user) return 0;
    const currentPlan = getCurrentPlan();
    if (!currentPlan || currentPlan.reviews_per_month === -1) return 0;
    return Math.round(Math.min((usage.current_month.reviews / currentPlan.reviews_per_month) * 100, 100));
  };

  const getUsageColor = () => {
    const percentage = getUsagePercentage();
    if (percentage >= 90) return 'red';
    if (percentage >= 75) return 'orange';
    return 'green';
  };

  const currentPlan = getCurrentPlan();


  const getTierDisplayName = (tier?: string | null) => {
    switch (tier) {
      case 'FREE_TRIAL': return t('admin.content.common.tiers.freeTrial');
      case 'STARTER': return t('admin.content.common.tiers.starter');
      case 'PRO': return t('admin.content.common.tiers.pro');
      case 'BUSINESS': return t('admin.content.common.tiers.business');
      case 'pro_plus': return t('admin.content.common.tiers.proPlus');
      case 'pro': return t('admin.content.common.tiers.pro');
      default: return tier || t('admin.content.common.tiers.free');
    }
  };

  const getPlanDisplayName = (plan?: SubscriptionPlan | null) => {
    if (!plan) return t('admin.content.common.tiers.free');
    switch (plan.tier) {
      case 'FREE_TRIAL':
      case 'STARTER':
      case 'PRO':
      case 'BUSINESS':
      case 'pro_plus':
      case 'pro':
        return getTierDisplayName(plan.tier);
      default:
        return plan.name || t('admin.content.common.tiers.free');
    }
  };

  // Show loading state if user is not loaded yet
  if (!user) {
    return (
      <Box p={8} textAlign="center">
        <Text>{t('admin.content.userSubscription.loadingUserInformation')}</Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Text>{t('admin.content.userSubscription.loadingSubscriptionInformation')}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8} textAlign="center">
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator>
            <Icon as={FiAlertCircle} />
          </Alert.Indicator>
          <Alert.Description>
            {error}
          </Alert.Description>
        </Alert.Root>
        <Button mt={4} onClick={loadData}>
          {t('admin.content.common.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <VStack gap={8} align="stretch">
      {/* Current Plan & Usage */}
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl">
        <Card.Header>
          <HStack justify="space-between">
            <Box>
              <Text fontSize="xl" fontWeight="bold">{t('admin.content.userSubscription.currentPlan.title')}</Text>
              <Text color="gray.500">{t('admin.content.userSubscription.currentPlan.subtitle')}</Text>
            </Box>
            <Badge
              colorScheme={user?.subscription_tier === 'pro_plus' ? 'purple' :
                          user?.subscription_tier === 'pro' ? 'blue' : 'gray'}
              size="lg"
              px={4}
              py={2}
            >
              {getTierDisplayName(user?.subscription_tier)}
            </Badge>
          </HStack>
        </Card.Header>
        <Card.Body>
          <VStack gap={6} align="stretch">
            {/* Plan Details */}
            {currentPlan ? (
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={2}>
                  {t('admin.content.userSubscription.currentPlan.planPricePerMonth', { plan: getPlanDisplayName(currentPlan), amount: (currentPlan.monthly_price / 100).toFixed(2) })}
                </Text>
                <Text color="gray.600" mb={4}>
                  {currentPlan.description}
                </Text>

                <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                  <Stat.Root>
                    <Stat.Label>{t('admin.content.userSubscription.usage.assessmentsThisMonth')}</Stat.Label>
                    <Stat.ValueText>{usage?.current_month?.reviews || 0}</Stat.ValueText>
                    <Stat.HelpText>
                      {currentPlan.reviews_per_month === -1 ? t('admin.content.common.unlimited') :
                       t('admin.content.userSubscription.usage.ofLimit', { count: currentPlan.reviews_per_month })}
                    </Stat.HelpText>
                  </Stat.Root>

                  <Stat.Root>
                    <Stat.Label>{t('admin.content.userSubscription.usage.maxFileSize')}</Stat.Label>
                    <Stat.ValueText>{currentPlan.max_document_size_mb}MB</Stat.ValueText>
                    <Stat.HelpText>{t('admin.content.userSubscription.usage.perDocument')}</Stat.HelpText>
                  </Stat.Root>

                  <Stat.Root>
                    <Stat.Label>{t('admin.content.userSubscription.usage.reportStorage')}</Stat.Label>
                    <Stat.ValueText>{t('admin.content.userSubscription.usage.daysCount', { count: currentPlan.report_storage_days })}</Stat.ValueText>
                    <Stat.HelpText>{t('admin.content.userSubscription.usage.retentionPeriod')}</Stat.HelpText>
                  </Stat.Root>
                </SimpleGrid>
              </Box>
            ) : (
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={2}>
                  {t('admin.content.userSubscription.freePlan.title')}
                </Text>
                <Text color="gray.600" mb={4}>
                  {t('admin.content.userSubscription.freePlan.description')}
                </Text>

                <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                  <Stat.Root>
                    <Stat.Label>{t('admin.content.userSubscription.usage.assessmentsThisMonth')}</Stat.Label>
                    <Stat.ValueText>{usage?.current_month?.reviews || 0}</Stat.ValueText>
                    <Stat.HelpText>{t('admin.content.userSubscription.usage.limitedAssessments')}</Stat.HelpText>
                  </Stat.Root>

                  <Stat.Root>
                    <Stat.Label>{t('admin.content.userSubscription.usage.maxFileSize')}</Stat.Label>
                    <Stat.ValueText>5MB</Stat.ValueText>
                    <Stat.HelpText>{t('admin.content.userSubscription.usage.perDocument')}</Stat.HelpText>
                  </Stat.Root>

                  <Stat.Root>
                    <Stat.Label>{t('admin.content.userSubscription.usage.reportStorage')}</Stat.Label>
                    <Stat.ValueText>{t('admin.content.userSubscription.usage.daysCount', { count: 30 })}</Stat.ValueText>
                    <Stat.HelpText>{t('admin.content.userSubscription.usage.retentionPeriod')}</Stat.HelpText>
                  </Stat.Root>
                </SimpleGrid>
              </Box>
            )}

            {/* Usage Progress */}
            {currentPlan && currentPlan.reviews_per_month !== -1 && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">{t('admin.content.userSubscription.usage.monthlyUsage')}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {usage?.current_month?.reviews || 0} / {currentPlan.reviews_per_month}
                  </Text>
                </HStack>
                <Progress.Root value={getUsagePercentage()}
                  colorScheme={getUsageColor()}
                  size="lg"
                  borderRadius="full"
                />
                {getUsagePercentage() >= 90 && (
                  <Alert.Root status="warning" mt={3} borderRadius="md">
                    <Alert.Indicator>
                      <Icon as={FiAlertTriangle} />
                    </Alert.Indicator>
                    <Alert.Description fontSize="sm">
                      {t('admin.content.userSubscription.usage.nearLimitWarning')}
                    </Alert.Description>
                  </Alert.Root>
                )}
              </Box>
            )}

            {/* Upgrade Button */}
            {user?.subscription_tier !== 'pro_plus' && (
              <Button
                colorScheme="blue"
                size="lg"
                onClick={() => setIsModalOpen(true)}
              >
                <FiTrendingUp /> {t('admin.content.userSubscription.actions.upgradePlan')}
              </Button>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>

      {/* Available Plans */}
      {plans.length > 0 && (
        <Card.Root bg={cardBg} shadow="lg" borderRadius="xl">
          <Card.Header>
            <Text fontSize="xl" fontWeight="bold">{t('admin.content.userSubscription.availablePlans.title')}</Text>
            <Text color="gray.500">{t('admin.content.userSubscription.availablePlans.subtitle')}</Text>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
              {plans.map((plan) => (
                <Card.Root key={plan.id}
                  border="2px solid"
                  borderColor={currentPlan?.id === plan.id ? 'blue.500' : borderColor}
                  bg={currentPlan?.id === plan.id ? 'blue.50' : cardBg}
                  _dark={{ bg: currentPlan?.id === plan.id ? 'blue.900' : cardBg }}
                >
                  <Card.Body>
                    <VStack gap={4} align="stretch">
                      <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="bold" color="blue.600">
                          {getPlanDisplayName(plan)}
                        </Text>
                        <Text fontSize="3xl" fontWeight="bold">
                          ${(plan.monthly_price / 100).toFixed(2)}
                          <Text as="span" fontSize="lg" color="gray.500">{t('admin.content.userSubscription.availablePlans.perMonthSuffix')}</Text>
                        </Text>
                        <Text color="gray.600" fontSize="sm">
                          {plan.description}
                        </Text>
                      </Box>

                      <Separator />

                      <List.Root gap={2}>
                        <List.Item>
                          <List.Indicator asChild color="green.500">
                            <Icon as={FiCheck} />
                          </List.Indicator>
                          {t('admin.content.userSubscription.availablePlans.assessmentsPerMonth', { count: plan.reviews_per_month === -1 ? t('admin.content.common.unlimited') : plan.reviews_per_month })}
                        </List.Item>
                        <List.Item>
                          <List.Indicator asChild color="green.500">
                            <Icon as={FiCheck} />
                          </List.Indicator>
                          {t('admin.content.userSubscription.availablePlans.maxFileSize', { count: plan.max_document_size_mb })}
                        </List.Item>
                        <List.Item>
                          <List.Indicator asChild color="green.500">
                            <Icon as={FiCheck} />
                          </List.Indicator>
                          {t('admin.content.userSubscription.availablePlans.documentsPerAssessment', { count: plan.max_documents_per_review })}
                        </List.Item>
                        <List.Item>
                          <List.Indicator asChild color="green.500">
                            <Icon as={FiCheck} />
                          </List.Indicator>
                          {t('admin.content.userSubscription.availablePlans.reportStorageDays', { count: plan.report_storage_days })}
                        </List.Item>
                        {plan.unlimited_standards && (
                          <List.Item>
                            <List.Indicator asChild color="green.500">
                              <Icon as={FiCheck} />
                            </List.Indicator>
                            {t('admin.content.userSubscription.availablePlans.unlimitedStandards')}
                          </List.Item>
                        )}
                      </List.Root>

                      <Button
                        colorScheme={currentPlan?.id === plan.id ? 'gray' : 'blue'}
                        variant={currentPlan?.id === plan.id ? 'outline' : 'solid'}
                        disabled={currentPlan?.id === plan.id}
                        onClick={() => {
                          setSelectedPlan(plan);
                          setIsModalOpen(true);
                        }}
                      >
                        {currentPlan?.id === plan.id ? t('admin.content.userSubscription.actions.currentPlan') : t('admin.content.userSubscription.actions.selectPlan')}
                      </Button>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          </Card.Body>
        </Card.Root>
      )}

      {/* Stripe Payment Integration */}
      {selectedPlan && (
        <StripePayment
          planId={selectedPlan.id}
          planName={selectedPlan.name}
          planPrice={selectedPlan.monthly_price / 100}
          planTier={selectedPlan.tier}
          planFeatures={[
            t('admin.content.userSubscription.availablePlans.assessmentsPerMonth', { count: selectedPlan.reviews_per_month === -1 ? t('admin.content.common.unlimited') : selectedPlan.reviews_per_month }),
            t('admin.content.userSubscription.availablePlans.maximumFileSize', { count: selectedPlan.max_document_size_mb }),
            t('admin.content.userSubscription.availablePlans.documentsPerAssessment', { count: selectedPlan.max_documents_per_review }),
            t('admin.content.userSubscription.availablePlans.reportStorageDays', { count: selectedPlan.report_storage_days }),
            ...(selectedPlan.unlimited_standards ? [t('admin.content.userSubscription.availablePlans.unlimitedStandardsAccess')] : [])
          ]}
          onSuccess={async () => {
            setIsModalOpen(false);
            try {
              // Refresh user data to get updated subscription tier
              await refreshUser();
              // Reload subscription data
              await loadData();
              toaster.create({
                title: t('admin.content.userSubscription.toasts.subscriptionUpdatedTitle'),
                description: t('admin.content.userSubscription.toasts.successfullyUpgraded', { plan: getPlanDisplayName(selectedPlan) }),
                type: 'success',
              });
            } catch (error) {
              console.error('Error refreshing user data:', error);
              toaster.create({
                title: t('admin.content.userSubscription.toasts.subscriptionUpdatedTitle'),
                description: t('admin.content.userSubscription.toasts.successfullyUpgradedRefresh', { plan: getPlanDisplayName(selectedPlan) }),
                type: 'success',
              });
            }
          }}
          onCancel={() => setIsModalOpen(false)}
        />
      )}
    </VStack>
  );
};

export default UserSubscriptionManager;
